import * as React from "react"

import { CAPABILITIES, type Capability } from "@ibada/shared-types/feature-flags"

import { useFeatureAccess } from "@/shared/feature-flags"
import type { ChurchNavSection } from "@/features/dashboard/model/church-nav"
import { useFinanceNavGate } from "@/features/finance/hooks/use-finance-nav-gate"

/**
 * Every capability in the registry, in a fixed order. Module-level and derived from a static object, so the
 * list length never changes between renders — which is what makes calling a hook per entry below legal.
 */
const CAPABILITY_KEYS = Object.keys(CAPABILITIES) as Capability[]

/**
 * Removes nav sections whose feature-flag `capability` is not currently available — the V1-paused features
 * (Budgets, Pledges, Assets, Work Queue, Reports, Rafiki). Permission gating is handled separately by
 * `filterVisibleChurchNavSections`; this is the flag layer, applied at the render site because feature-flag
 * resolution is hook-based. UX only: hiding the link is not the security — the routes are guarded too.
 *
 * This used to name each capability in an if-chain (`reports.view`, `forms.view`), which meant tagging a nav
 * section with any OTHER capability silently did nothing: the flag existed, the section stayed visible, and the
 * first sign of trouble was the route guard bouncing the user AFTER they clicked. Resolving every registered
 * capability makes the filter total — a new flag works the moment a section is tagged with it.
 */
export function useNavCapabilityFilter(): (sections: ChurchNavSection[]) => ChurchNavSection[] {
  const allowed: Record<string, boolean> = {}
  for (const capability of CAPABILITY_KEYS) {
    // CAPABILITY_KEYS is a module-level constant, so the number and order of hook calls is identical on every
    // render — which is the condition the rule protects.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    allowed[capability] = useFeatureAccess(capability).allowed
  }

  const allowedKey = CAPABILITY_KEYS.map((capability) => (allowed[capability] ? "1" : "0")).join("")

  // Applied AFTER capability filtering: a branch that hasn't finished the minimum finance foundation shows only
  // "Configure Finance" under Finance. UX only — the finance routes are guarded regardless.
  const financeNavGate = useFinanceNavGate()

  return React.useCallback(
    (sections: ChurchNavSection[]) =>
      financeNavGate(
        sections
          .filter((section) => (section.capability ? allowed[section.capability] !== false : true))
          // A paused feature can live INSIDE a shipped section — Pledges is a child of Finance — so items must be
          // filtered too, or Finance would keep a link to a route the guard then bounces the user off.
          .map((section) => ({
            ...section,
            items: section.items.filter((item) =>
              item.capability ? allowed[item.capability] !== false : true,
            ),
          })),
      ),
    // `allowedKey` is a stable primitive summary of the map above; the map itself is a new object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowedKey, financeNavGate],
  )
}
